import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ListTree, GitBranch, Activity } from 'lucide-react';
import { hubApi, healthApi } from '@/services/api.js';
import { Card, CardHeader, StatCard, QuickAction, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import { fmtRelative, fmtNumber } from '@/utils/format.js';

export default function HubHome() {
  const { t } = useTranslation();
  const dashQuery = useQuery({
    queryKey: ['hub', 'dashboard'],
    queryFn:  () => hubApi.dashboard(),
    refetchInterval: 30000,
    retry: 1,
  });

  const auditQuery = useQuery({
    queryKey: ['hub', 'audit', 'recent'],
    queryFn:  () => hubApi.audit({ per_page: 8 }),
    retry: 1,
  });

  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn:  () => healthApi.ping(),
    refetchInterval: 60000,
    retry: 1,
  });

  const stats   = dashQuery.data        ?? {};
  const recent  = auditQuery.data?.data ?? [];

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('home.hubHome.hubAdminDashboard')}</h1>
        <p className="mt-1.5 text-sm text-slate-600">{t('home.hubHome.crossSubsystemOperationalOverviewMasterAudit')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4 mb-8">
        <StatCard label={t('home.hubHome.reportsToday')}     value={dashQuery.isLoading ? '…' : fmtNumber(stats.reports_today)}    hint={t('home.hubHome.rerrIntake')}        accent="rose" />
        <StatCard label={t('home.hubHome.activePartners')}   value={dashQuery.isLoading ? '…' : fmtNumber(stats.active_partners)}  hint={t('home.hubHome.federatedTenants')}  accent="emerald" />
        <StatCard label={t('home.hubHome.auditEvents24h')}  value={dashQuery.isLoading ? '…' : fmtNumber(stats.audit_24h)}        hint={t('home.hubHome.appendOnlyLog')}    accent="sky" />
        <StatCard
          label={t('home.hubHome.apiHealth')}
          value={healthQuery.isLoading ? '…' : (healthQuery.data?.status === 'operational' ? 'OK' : (healthQuery.data?.status || '—'))}
          hint={healthQuery.data?.version ? `v${healthQuery.data.version}` : 'NACIS API'}
          accent={healthQuery.data?.status === 'operational' ? 'emerald' : 'amber'}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-10">
        <QuickAction to="/hub/audit"   title={t('home.hubHome.masterAudit')}   description={t('home.hubHome.browseFilterAndExportTheAppend')}     icon={ListTree} />
        <QuickAction to="/hub/routing" title={t('home.hubHome.routingLog')}    description={t('home.hubHome.interAgencyRoutingManifestsAndPropagation')} icon={GitBranch} />
        <QuickAction to="/hub"         title={t('home.hubHome.liveOverview')}  description={t('home.hubHome.crossSubsystemHealthAndThroughputAt')}        icon={Activity} />
      </div>

      <Card>
        <CardHeader title={t('home.hubHome.recentAuditEvents')} description={t('home.hubHome.mostRecentEntriesFromTheMaster')} />
        <div className="mt-5">
          {auditQuery.isLoading ? (
            <SkeletonList rows={6} />
          ) : auditQuery.isError ? (
            <ErrorPanel error={auditQuery.error} onRetry={auditQuery.refetch} />
          ) : recent.length === 0 ? (
            <EmptyState title={t('home.hubHome.noAuditEvents')} description={t('home.hubHome.whenSubsystemsWritePrivilegedActionsThey')} />
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {recent.map(e => (
                <li key={e.id} className="grid grid-cols-12 items-center gap-3 py-3">
                  <div className="col-span-3 truncate font-medium text-slate-900">{e.subsystem || '—'}</div>
                  <div className="col-span-3 truncate text-slate-700">{e.action}</div>
                  <div className="col-span-4 truncate text-slate-500">
                    {e.entity_type}{e.entity_id ? ` · ${String(e.entity_id).slice(0, 8)}` : ''}
                  </div>
                  <div className="col-span-2 truncate text-right text-xs text-slate-500">{fmtRelative(e.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="grid grid-cols-12 items-center gap-3 py-3">
          <div className="col-span-3 h-3 rounded-full bg-slate-200 animate-pulse" />
          <div className="col-span-3 h-3 rounded-full bg-slate-100 animate-pulse" />
          <div className="col-span-4 h-3 rounded-full bg-slate-100 animate-pulse" />
          <div className="col-span-2 h-3 rounded-full bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}
